/** @prettier */
import React, { useState, useMemo } from "react";
import ReactTable, { Column } from "react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Pagination from "../../CommonComponents/Pagination";
import { Nav, NavItem } from "react-bootstrap";
import { QuestionPerformanceResult } from "../api";
import { Link } from "react-router-dom";

const headerStyle = { fontSize: "17px" };

type TableCell = (questionData: QuestionPerformanceResult) => React.ReactNode;
const buildColumns = (
    actionsCell: TableCell,
): Array<Column<QuestionPerformanceResult>> => [
    {
        id: "result",
        Header: "Result",
        headerStyle,
        width: 100,
        className: "tre-result-cell cell-center",
        accessor: ({ result }): JSX.Element => {
            return result === "CORRECT" ? (
                <FontAwesomeIcon
                    icon={["far", "check-circle"]}
                    title="Correct"
                    className="correct"
                />
            ) : result === "INCORRECT" ? (
                <FontAwesomeIcon
                    icon={["far", "times-circle"]}
                    title="Incorrect"
                    className="incorrect"
                />
            ) : result === "UNANSWERED" ? (
                <FontAwesomeIcon
                    icon="exclamation-circle"
                    title="Unanswered"
                    className="unanswered"
                />
            ) : (
                // if we've handled all cases, result is never, so this compiles
                result
            );
        },
    },
    {
        Header: "Question",
        id: "number",
        headerStyle,
        className: "cell-center",
        width: 100,
        accessor: ({ number }) => number,
    },
    {
        Header: "Flagged",
        id: "flagged",
        headerStyle,
        className: "cell-center",
        width: 100,
        accessor: ({ flagged }) => {
            return flagged ? <FontAwesomeIcon icon={["far", "flag"]} /> : null;
        },
    },
    {
        Header: "Category",
        accessor: "category",
        headerStyle,
    },
    {
        Header: "Subcategory",
        accessor: "subcategory",
        headerStyle,
    },
    {
        Header: "Topic",
        accessor: "topic",
        headerStyle,
    },
    {
        id: "actions",
        Header: "Actions",
        headerStyle,
        width: 100,
        className: "cell-center",
        accessor: actionsCell,
    },
];

type ReviewQuestionListProps = {
    questions: QuestionPerformanceResult[];
    testId: string;
};
type FilterState = "ALL" | "FLAGGED" | "CORRECT" | "INCORRECT" | "UNANSWERED";
const ReviewQuestionList = ({ questions, testId }: ReviewQuestionListProps) => {
    const [filterState, setFilterState] = useState<FilterState>("ALL");

    const filteredQuestions = useMemo((): QuestionPerformanceResult[] => {
        if (filterState === "ALL") return questions;
        return questions.filter((question) => {
            return filterState === "FLAGGED"
                ? question.flagged
                : question.result === filterState;
        });
    }, [questions, filterState]);

    const columns = useMemo(
        () =>
            buildColumns(({ number }) => (
                <Link
                    title={`Review question ${number}`}
                    to={`/tests/${testId}/review?number=${number}`}
                >
                    <FontAwesomeIcon icon="eye" />
                </Link>
            )),
        [testId],
    );

    return (
        <>
            <Nav
                id="tre-question-list-nav"
                activeKey={filterState}
                // bug in the types here: onSelect is typed as (Event) => void
                // but is actually (eventKey) => void
                onSelect={setFilterState as any}
                bsStyle="pills"
            >
                <NavItem eventKey="ALL">All</NavItem>
                <NavItem eventKey="CORRECT">Correct</NavItem>
                <NavItem eventKey="INCORRECT">Incorrect</NavItem>
                <NavItem eventKey="UNANSWERED">Unanswered</NavItem>
                <NavItem eventKey="FLAGGED">Flagged</NavItem>
            </Nav>
            <ReactTable
                // Use the filterState as a key, so that we reset the table
                // pagination state if the filter changes
                key={filterState}
                className="tre-question-list-table"
                data={filteredQuestions}
                columns={columns}
                PaginationComponent={Pagination}
                minRows={10}
                pageSize={10}
            />
        </>
    );
};
export default ReviewQuestionList;
